/**
 * Is this request path an asset probe rather than a CMS page?
 *
 * WHY. `/favicon.ico` and friends have no file in `public/`, so they fall through to the
 * `[slug1]` route, which asks the CMS for a page called "favicon.ico" and gets a 404.
 *
 * WHAT THIS ACTUALLY SAVES: the CMS round-trip, and only that. Measured on one dev server,
 * guarded vs an unguarded missing page:
 *
 *     /favicon.ico           guarded     0 page-API calls   48,346 B
 *     /this-does-not-exist   unguarded   1 page-API call    48,617 B
 *
 * So do NOT read this as a bytes fix — the ~48 KB (~64 KB on a production build) is what
 * this site returns for ANY bad URL, and it is unchanged. The win is that the wasted call
 * disappears: that endpoint runs ~27 DB queries upstream, and a 404 is never cached (Next
 * only caches 200s), so every probe was a genuine origin hit every single time. Browsers
 * request favicons unprompted and iOS asks for the apple-touch icons on its own, so this
 * fired constantly for real visitors.
 *
 * WHY NOT MIDDLEWARE, which would also kill the bytes: middleware runs BEFORE static file
 * serving, so a matcher broad enough to catch `*.png` would 404 every image in `public/`.
 * A guard inside the dynamic route is inherently safe — a request only reaches it after
 * `public/` and every real route (robots.txt, sitemap.xml) have already declined it.
 *
 * WHY AN EXPLICIT LIST AND NOT /\.[a-z]+$/. A generic "ends in a dot-extension" test would
 * also reject legitimate slugs. `b.tech` is an entirely plausible page on an education site,
 * and marketing can create it in the CMS tomorrow without a deploy — a regex would 404 it
 * silently and nobody would connect the two. Only extensions that are unambiguously files
 * belong here. When in doubt, leave it out: a missed probe costs one wasted request, a false
 * positive takes a real page offline.
 */
const ASSET_EXTENSIONS = new Set([
  // icons and images — the ones browsers request on their own
  "ico", "png", "jpg", "jpeg", "gif", "svg", "webp", "avif", "bmp",
  // web app / crawler manifests
  "json", "webmanifest", "xml", "txt",
  // code and maps
  "css", "js", "mjs", "map",
  // fonts
  "woff", "woff2", "ttf", "otf", "eot",
  // media and documents
  "mp4", "webm", "mp3", "pdf", "zip",
  // common bot probes for stacks we do not run
  "php", "asp", "aspx", "env",
]);

/**
 * True when the final path segment ends in a known asset extension.
 *
 * Takes the joined slug string as the page routes build it ("a/b/c"), so it judges only the
 * LAST segment — a directory called `images` is a fine page slug, `images/logo.png` is not.
 */
export function isAssetPath(slugs: string | undefined | null): boolean {
  if (!slugs) return false;
  const last = slugs.split("/").filter(Boolean).pop();
  if (!last) return false;

  const dot = last.lastIndexOf(".");
  // No dot, or a trailing dot with nothing after it ("online-mba." is a typo'd page, not a
  // file) — treat as a page and let the CMS decide. `dot === 0` IS matched deliberately, so
  // that bot probes for dotfiles like `/.env` are caught; no CMS slug starts with a dot.
  if (dot === -1 || dot === last.length - 1) return false;

  return ASSET_EXTENSIONS.has(last.slice(dot + 1).toLowerCase());
}
